import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { usePermissions } from "@/context/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  UserPlus,
  Users,
  Search,
  Filter,
  Phone,
  Mail,
  Shield,
  Eye,
  EyeOff,
  ChevronDown,
  CheckCircle,
  XCircle,
  Clock,
  PauseCircle,
  Key,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roleOptions = [
  { value: "all", label: "All Roles" },
  { value: "admin", label: "Administrator" },
  { value: "case_manager", label: "Case Manager" },
  { value: "rn", label: "Registered Nurse" },
  { value: "pcw", label: "Personal Care Worker" },
];

const roleLabels = {
  admin: "Administrator",
  case_manager: "Case Manager",
  rn: "Registered Nurse",
  pcw: "Personal Care Worker",
};

const roleBadgeClasses = {
  admin: "role-admin",
  case_manager: "role-case_manager",
  rn: "role-rn",
  pcw: "role-pcw",
};

const statusOptions = [
  { value: "active", label: "Active", icon: CheckCircle, color: "text-green-500" },
  { value: "inactive", label: "Inactive", icon: XCircle, color: "text-red-500" },
  { value: "on_hold", label: "On Hold", icon: PauseCircle, color: "text-violet-500" },
  { value: "on_leave", label: "On Leave", icon: Clock, color: "text-amber-500" },
];

export default function EmployeesPage() {
  const { user } = useAuth();
  const { canCreate } = usePermissions();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  
  // Password reset dialog state
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordData, setResetPasswordData] = useState({
    employeeId: null,
    employeeName: "",
    tempPassword: null,
  });
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Status change dialog state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusChangeData, setStatusChangeData] = useState({
    employeeId: null,
    employeeName: "",
    newStatus: "",
    effectiveDate: new Date().toISOString().split('T')[0],
    reason: "",
  });
  
  const [newEmployee, setNewEmployee] = useState({
    name: "",
    email: "",
    password: "",
    role: "pcw",
    phone: "",
    license_number: "",
    license_expiry: "",
  });

  useEffect(() => {
    fetchEmployees();
  }, [roleFilter]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter && roleFilter !== "all") {
        params.append("role", roleFilter);
      }
      
      const response = await axios.get(`${API}/employees?${params.toString()}`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (e, employeeId, employeeName, newStatus) => {
    e.preventDefault();
    e.stopPropagation();

    // For any non-active state, prompt for effective date + reason
    if (newStatus === "inactive" || newStatus === "on_leave" || newStatus === "on_hold") {
      setStatusChangeData({
        employeeId,
        employeeName,
        newStatus,
        effectiveDate: new Date().toISOString().split('T')[0],
        reason: "",
      });
      setStatusDialogOpen(true);
    } else {
      // If changing to active, update immediately
      submitStatusChange(employeeId, newStatus, null, null);
    }
  };

  const submitStatusChange = async (employeeId, newStatus, effectiveDate, reason) => {
    setUpdatingStatus(employeeId);
    try {
      const isActive = newStatus === "active";
      // Preserve the specific status verb (on_leave / on_hold / inactive)
      // even though is_active goes false for all non-active states.
      let status;
      if (newStatus === "on_leave") status = "on_leave";
      else if (newStatus === "on_hold") status = "on_hold";
      else status = isActive ? "active" : "inactive";
      
      const updateData = {
        is_active: isActive,
        status: status
      };
      
      // Add date and reason if provided
      if (effectiveDate) {
        updateData.status_change_date = effectiveDate;
      }
      if (reason) {
        updateData.status_change_reason = reason;
      }
      
      await axios.put(`${API}/employees/${employeeId}`, updateData);
      
      // Update local state
      setEmployees(prev => prev.map(emp => 
        emp.id === employeeId 
          ? { 
              ...emp, 
              is_active: isActive, 
              status: status,
              status_change_date: effectiveDate,
              status_change_reason: reason
            }
          : emp
      ));
      
      const statusLabel = statusOptions.find(s => s.value === newStatus)?.label || newStatus;
      toast.success(`Employee status updated to ${statusLabel}`);
      setStatusDialogOpen(false);
    } catch (error) {
      console.error("Failed to update employee status:", error);
      toast.error("Failed to update employee status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleStatusDialogSubmit = (e) => {
    e.preventDefault();
    submitStatusChange(
      statusChangeData.employeeId,
      statusChangeData.newStatus,
      statusChangeData.effectiveDate,
      statusChangeData.reason
    );
  };

  const getEmployeeStatus = (employee) => {
    if (employee.status === "on_leave") return "on_leave";
    if (employee.status === "on_hold") return "on_hold";
    return employee.is_active ? "active" : "inactive";
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();

    if (!newEmployee.name || !newEmployee.email || !newEmployee.password) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (newEmployee.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setCreating(true);
    try {
      const created = await axios.post(`${API}/employees`, newEmployee);
      const autoForms = created.data?.auto_assigned_forms || [];
      toast.success(
        autoForms.length
          ? `Employee created — ${autoForms.length} new-hire form${autoForms.length === 1 ? "" : "s"} assigned (${autoForms.join(", ")})`
          : "Employee created successfully!"
      );
      if (created.data?.linked_existing_login) {
        toast.info("This email already had a login in your agency — it was linked to the new employee record and its password updated.");
      }
      setDialogOpen(false);
      setNewEmployee({
        name: "",
        email: "",
        password: "",
        role: "pcw",
        phone: "",
        license_number: "",
        license_expiry: "",
      });
      fetchEmployees();
    } catch (error) {
      console.error("Failed to create employee:", error);
      toast.error(error.response?.data?.detail || "Failed to create employee");
    } finally {
      setCreating(false);
    }
  };

  const handleResetPassword = async (employeeId, employeeName) => {
    setResetPasswordData({
      employeeId,
      employeeName,
      tempPassword: null,
    });
    setResetPasswordDialogOpen(true);
  };

  const confirmResetPassword = async () => {
    setResettingPassword(true);
    try {
      const response = await axios.post(`${API}/users/${resetPasswordData.employeeId}/reset-password`);
      setResetPasswordData(prev => ({
        ...prev,
        tempPassword: response.data.temp_password
      }));
      toast.success("Password reset successfully!");
    } catch (error) {
      console.error("Failed to reset password:", error);
      const errorMsg = error.response?.data?.detail || "Failed to reset password";
      toast.error(errorMsg);
      setResetPasswordDialogOpen(false);
    } finally {
      setResettingPassword(false);
    }
  };

  const filteredEmployees = employees
    .filter((emp) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        emp.name.toLowerCase().includes(query) ||
        emp.email.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase()));

  const isAdmin = user?.role === "admin";
  const canAddEmployee = canCreate("employees");

  return (
    <Layout>
      {/* Page Header */}
      <div className="page-header">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="page-title">Employees</h1>
            <p className="page-subtitle">
              Manage your agency staff and their roles.
            </p>
          </div>
          {canAddEmployee && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2" data-testid="add-employee-btn">
                  <UserPlus className="w-4 h-4" />
                  Add Employee
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Add New Employee</DialogTitle>
                  <DialogDescription>
                    Create a new employee account for your agency.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateEmployee}>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="emp-name">Full Name *</Label>
                      <Input
                        id="emp-name"
                        value={newEmployee.name}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, name: e.target.value }))
                        }
                        required
                        data-testid="emp-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emp-email">Email *</Label>
                      <Input
                        id="emp-email"
                        type="email"
                        value={newEmployee.email}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, email: e.target.value }))
                        }
                        required
                        data-testid="emp-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emp-password">Password *</Label>
                      <div className="relative">
                        <Input
                          id="emp-password"
                          type={showPassword ? "text" : "password"}
                          value={newEmployee.password}
                          onChange={(e) =>
                            setNewEmployee((prev) => ({
                              ...prev,
                              password: e.target.value,
                            }))
                          }
                          required
                          data-testid="emp-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emp-role">Role *</Label>
                      <Select
                        value={newEmployee.role}
                        onValueChange={(value) =>
                          setNewEmployee((prev) => ({ ...prev, role: value }))
                        }
                      >
                        <SelectTrigger data-testid="emp-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Administrator</SelectItem>
                          <SelectItem value="case_manager">Case Manager</SelectItem>
                          <SelectItem value="hr_scheduler">HR/Scheduler</SelectItem>
                          <SelectItem value="evv_specialist">EVV Specialist</SelectItem>
                          <SelectItem value="intake_coordinator">Intake Coordinator</SelectItem>
                          <SelectItem value="receptionist">Receptionist</SelectItem>
                          <SelectItem value="rn">Registered Nurse</SelectItem>
                          <SelectItem value="pcw">Personal Care Worker</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="emp-phone">Phone</Label>
                      <Input
                        id="emp-phone"
                        type="tel"
                        value={newEmployee.phone}
                        onChange={(e) =>
                          setNewEmployee((prev) => ({ ...prev, phone: e.target.value }))
                        }
                        data-testid="emp-phone-input"
                      />
                    </div>
                    {(newEmployee.role === "rn" || newEmployee.role === "pcw") && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="emp-license">License Number</Label>
                          <Input
                            id="emp-license"
                            value={newEmployee.license_number}
                            onChange={(e) =>
                              setNewEmployee((prev) => ({
                                ...prev,
                                license_number: e.target.value,
                              }))
                            }
                            data-testid="emp-license-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="emp-license-expiry">License Expiry</Label>
                          <Input
                            id="emp-license-expiry"
                            type="date"
                            value={newEmployee.license_expiry}
                            onChange={(e) =>
                              setNewEmployee((prev) => ({
                                ...prev,
                                license_expiry: e.target.value,
                              }))
                            }
                            data-testid="emp-license-expiry-input"
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={creating} data-testid="create-emp-btn">
                      {creating ? "Creating..." : "Create Employee"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="employee-search-input"
            />
          </div>

          {/* Role Filter */}
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full md:w-48" data-testid="role-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Employee List */}
      <div className="bg-white rounded-xl border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : filteredEmployees.length === 0 ? (
          <div className="empty-state py-16">
            <div className="empty-state-icon">
              <Users className="w-8 h-8 text-slate-400" />
            </div>
            <p className="empty-state-title">No employees found</p>
            <p className="empty-state-description">
              {roleFilter !== "all"
                ? `No employees with "${roleLabels[roleFilter]}" role.`
                : "Start by adding your first employee."}
            </p>
            {canAddEmployee && (
              <Button
                size="sm"
                onClick={() => setDialogOpen(true)}
                className="mt-4"
                data-testid="empty-add-employee-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Employee
              </Button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {/* Table Header - Desktop */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-400">
              <div className="col-span-4">Employee</div>
              <div className="col-span-3">Contact</div>
              <div className="col-span-2">Role</div>
              <div className="col-span-2">License</div>
              <div className="col-span-1">Status</div>
            </div>

            {/* Employee Rows */}
            {filteredEmployees.map((employee) => (
              <Link
                to={`/employees/${employee.id}`}
                key={employee.id}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-slate-50 transition-colors cursor-pointer"
                data-testid={`employee-row-${employee.id}`}
              >
                {/* Employee Info */}
                <div className="md:col-span-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium flex-shrink-0">
                    {employee.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2)}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{employee.name}</p>
                    <p className="text-sm text-slate-500">{employee.email}</p>
                  </div>
                </div>

                {/* Contact */}
                <div className="md:col-span-3 space-y-1">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{employee.phone || "No phone"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600 md:hidden">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span>{employee.email}</span>
                  </div>
                </div>

                {/* Role */}
                <div className="md:col-span-2">
                  <span className={`badge ${roleBadgeClasses[employee.role] || ""}`}>
                    {roleLabels[employee.role] || employee.role}
                  </span>
                </div>

                {/* License */}
                <div className="md:col-span-2 text-sm text-slate-600">
                  {employee.license_number ? (
                    <div>
                      <p className="font-medium">{employee.license_number}</p>
                      {employee.license_expiry && (
                        <p className="text-xs text-slate-400">
                          Exp: {employee.license_expiry}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className="text-slate-400">N/A</span>
                  )}
                </div>

                {/* Status */}
                <div className="md:col-span-1" onClick={(e) => e.preventDefault()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-7 px-2 gap-1 ${
                          getEmployeeStatus(employee) === "active"
                            ? "text-green-600 hover:text-green-700 hover:bg-green-50"
                            : getEmployeeStatus(employee) === "on_leave"
                            ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            : getEmployeeStatus(employee) === "on_hold"
                            ? "text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                            : "text-red-600 hover:text-red-700 hover:bg-red-50"
                        }`}
                        disabled={updatingStatus === employee.id}
                        data-testid={`status-dropdown-${employee.id}`}
                      >
                        {updatingStatus === employee.id ? (
                          <span className="text-xs">...</span>
                        ) : (
                          <>
                            {getEmployeeStatus(employee) === "active" && <CheckCircle className="w-3 h-3" />}
                            {getEmployeeStatus(employee) === "inactive" && <XCircle className="w-3 h-3" />}
                            {getEmployeeStatus(employee) === "on_leave" && <Clock className="w-3 h-3" />}
                            {getEmployeeStatus(employee) === "on_hold" && <PauseCircle className="w-3 h-3" />}
                            <span className="text-xs font-medium">
                              {statusOptions.find(s => s.value === getEmployeeStatus(employee))?.label || "Active"}
                            </span>
                            <ChevronDown className="w-3 h-3" />
                          </>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-36">
                      {statusOptions.map((status) => {
                        const Icon = status.icon;
                        return (
                          <DropdownMenuItem
                            key={status.value}
                            onClick={(e) => handleStatusChange(e, employee.id, employee.name, status.value)}
                            className="gap-2 cursor-pointer"
                            data-testid={`status-option-${status.value}`}
                          >
                            <Icon className={`w-4 h-4 ${status.color}`} />
                            <span>{status.label}</span>
                            {getEmployeeStatus(employee) === status.value && (
                              <CheckCircle className="w-3 h-3 ml-auto text-primary" />
                            )}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  {/* Reset Password Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-slate-500 hover:text-teal-600 hover:bg-teal-50"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleResetPassword(employee.id, employee.name);
                    }}
                    data-testid={`reset-password-${employee.id}`}
                  >
                    <Key className="w-3 h-3 mr-1" />
                    <span className="text-xs">Reset PW</span>
                  </Button>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Results Count */}
      {!loading && filteredEmployees.length > 0 && (
        <p className="text-sm text-slate-500 mt-4">
          Showing {filteredEmployees.length} employee
          {filteredEmployees.length !== 1 ? "s" : ""}
        </p>
      )}

      {/* Status Change Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {statusChangeData.newStatus === "inactive" && <XCircle className="w-5 h-5 text-red-500" />}
              {statusChangeData.newStatus === "on_hold" && <PauseCircle className="w-5 h-5 text-violet-500" />}
              {statusChangeData.newStatus === "on_leave" && <Clock className="w-5 h-5 text-amber-500" />}
              {statusChangeData.newStatus === "inactive" && "Mark as Inactive"}
              {statusChangeData.newStatus === "on_hold" && "Mark as On Hold"}
              {statusChangeData.newStatus === "on_leave" && "Mark as On Leave"}
            </DialogTitle>
            <DialogDescription>
              Update status for <span className="font-medium">{statusChangeData.employeeName}</span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleStatusDialogSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="status-date">
                  {statusChangeData.newStatus === "inactive" && "Inactive Since"}
                  {statusChangeData.newStatus === "on_hold" && "On Hold Since"}
                  {statusChangeData.newStatus === "on_leave" && "Leave Start Date"} *
                </Label>
                <Input
                  id="status-date"
                  type="date"
                  value={statusChangeData.effectiveDate}
                  onChange={(e) =>
                    setStatusChangeData((prev) => ({ ...prev, effectiveDate: e.target.value }))
                  }
                  required
                  data-testid="status-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status-reason">
                  Reason {statusChangeData.newStatus === "on_leave" && "(Optional)"}
                </Label>
                <textarea
                  id="status-reason"
                  value={statusChangeData.reason}
                  onChange={(e) =>
                    setStatusChangeData((prev) => ({ ...prev, reason: e.target.value }))
                  }
                  placeholder={
                    statusChangeData.newStatus === "inactive"
                      ? "e.g., Resigned, Terminated, End of contract..."
                      : statusChangeData.newStatus === "on_hold"
                      ? "e.g., Background check pending, Documentation lapsed, Under review..."
                      : "e.g., Medical leave, Vacation, Family emergency..."
                  }
                  className="w-full min-h-[100px] px-3 py-2 text-sm rounded-md border border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="status-reason-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStatusDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={updatingStatus}
                className={
                  statusChangeData.newStatus === "inactive"
                    ? "bg-red-600 hover:bg-red-700"
                    : statusChangeData.newStatus === "on_hold"
                    ? "bg-violet-600 hover:bg-violet-700"
                    : "bg-amber-600 hover:bg-amber-700"
                }
                data-testid="confirm-status-btn"
              >
                {updatingStatus ? "Updating..." : "Confirm"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setResetPasswordData({ employeeId: null, employeeName: "", tempPassword: null });
        }
        setResetPasswordDialogOpen(open);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-teal-600" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              {resetPasswordData.tempPassword 
                ? `New password for ${resetPasswordData.employeeName}`
                : `Reset password for ${resetPasswordData.employeeName}?`
              }
            </DialogDescription>
          </DialogHeader>
          
          {resetPasswordData.tempPassword ? (
            <div className="space-y-4">
              <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                <p className="text-sm text-slate-600 mb-2">New temporary password:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-white rounded border text-lg font-mono tracking-wider">
                    {resetPasswordData.tempPassword}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(resetPasswordData.tempPassword);
                      toast.success("Password copied to clipboard!");
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-amber-600 mt-2">
                  ⚠️ Share this password securely with the employee. They should change it after first login.
                </p>
              </div>
              <DialogFooter>
                <Button onClick={() => {
                  setResetPasswordDialogOpen(false);
                  setResetPasswordData({ employeeId: null, employeeName: "", tempPassword: null });
                }}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                This will generate a new temporary password for this caregiver. The old password will no longer work.
              </p>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setResetPasswordDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={confirmResetPassword}
                  disabled={resettingPassword}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {resettingPassword ? "Resetting..." : "Reset Password"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
